import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  CommonInput,
  CommonSelect,
  CommonDatePicker,
  CommonButton,
  CommonModal,
} from "../Input";
import Swal from "sweetalert2";

const IndividualCustomerModal = ({ show, onHide, customer }) => {
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});

  const fileInputRefs = {
    frontId: React.useRef(null),
    backId: React.useRef(null),
    selfie: React.useRef(null),
    proofAddress: React.useRef(null),
  };

  useEffect(() => {
    if (show) {
      if (customer) {
        setFormData({
          firstName: customer.fullName?.split(" ")[0] || "",
          lastName: customer.fullName?.split(" ").slice(1).join(" ") || "",
          dob: customer.dob || "",
          nationality: { label: customer.nationality, value: customer.nationality },
          gender: { label: customer.gender || "Male", value: customer.gender || "male" },
          occupation: { label: customer.jobTitle, value: customer.jobTitle },
          phonePrefix: { label: "+65", value: "+65" },
          phone: customer.phone || "",
          email: customer.email || "",
          country: { label: customer.country, value: customer.country },
          city: customer.city || "",
          addressLine1: customer.addressLine1 || "",
          addressLine2: customer.addressLine2 || "",
          postalCode: customer.postalCode || "",
          idType: { label: customer.idType, value: customer.idType },
          idNumber: customer.idNumber || "",
          issueDate: customer.issueDate || "",
        });
      } else {
        setFormData({
          phonePrefix: { label: "+65", value: "+65" },
          gender: { label: "Male", value: "male" },
        });
      }
    }
  }, [show, customer]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const options = {
    nationality: [
      { value: "singapore", label: "Singaporean" },
      { value: "vietnamese", label: "Vietnamese" },
      { value: "malaysian", label: "Malaysian" },
    ],
    gender: [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "other", label: "Other" },
    ],
    occupation: [
      { value: "engineer", label: "Engineer" },
      { value: "teacher", label: "Teacher" },
      { value: "doctor", label: "Doctor" },
      { value: "student", label: "Student" },
    ],
    country: [
      { value: "SG", label: "Singapore" },
      { value: "VN", label: "Vietnam" },
      { value: "MY", label: "Malaysia" },
    ],
    idType: [
      { value: "nric", label: "NRIC" },
      { value: "passport", label: "Passport" },
      { value: "fin", label: "FIN" },
    ],
    phonePrefix: [
      { value: "+65", label: "+65" },
      { value: "+84", label: "+84" },
      { value: "+60", label: "+60" },
    ],
  };

  const handleFileClick = (refKey) => {
    fileInputRefs[refKey].current.click();
  };

  const handleFileChange = (refKey, e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null;
      setFiles((prev) => ({ ...prev, [refKey]: { file, preview } }));
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    Swal.fire({
      icon: "success",
      title: "Success",
      text: customer 
        ? "Individual customer updated successfully!" 
        : "Individual customer created successfully!",
      confirmButtonColor: "var(--primary)",
    }).then(() => {
      onHide();
    });
  };

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      title={customer ? "Edit Individual Customer" : "Create New Customer"}
      headerClassName="border-bottom pb-3"
      footer={
        <>
          <CommonButton variant="outline" onClick={onHide} className="me-2">
            Close
          </CommonButton>
          <CommonButton variant="primary" onClick={handleSubmit}>
            {customer ? "Update Customer" : "Create Customer"}
          </CommonButton>
        </>
      }
    >
      <p className="mb-4 text-muted fs-14">
        {customer 
          ? "Update the customer information below." 
          : "Fill in the customer information to create a new individual customer."}
      </p>
      <form onSubmit={handleSubmit}>
        {/* Customer Type Section */}
        <div className="mb-4">
          <CommonInput
            label="Customer Type"
            value="Individual"
            readOnly
            wrapClass="w-100"
          />
        </div>

        {/* Personal Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Personal Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <CommonInput 
              label="First Name" 
              placeholder="Enter first name" 
              value={formData.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label={
                <span>
                  Middle Name{" "}
                  <i className="fas fa-info-circle text-muted ms-1 fs-12"></i>
                </span>
              }
              placeholder="Enter middle name"
              value={formData.middleName}
              onChange={(e) => handleChange("middleName", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput 
              label="Last Name" 
              placeholder="Enter last name" 
              value={formData.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonDatePicker 
              label="Date of Birth" 
              value={formData.dob}
              onChange={(val) => handleChange("dob", val)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonSelect
              label="Nationality"
              placeholder="Select an option"
              options={options.nationality}
              value={formData.nationality}
              onChange={(val) => handleChange("nationality", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Country of Birth"
              placeholder="Select an option"
              options={options.country}
              value={formData.countryOfBirth}
              onChange={(val) => handleChange("countryOfBirth", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Gender"
              placeholder="Select an option"
              options={options.gender}
              value={formData.gender}
              onChange={(val) => handleChange("gender", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonSelect
              label="Occupation"
              placeholder="Select an option"
              options={options.occupation}
              value={formData.occupation}
              onChange={(val) => handleChange("occupation", val)}
            />
          </Col>
        </Row>

        {/* Contact Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Contact Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={6} className="mb-3">
            <label className="text-black fw-bold mb-1">Phone Number</label>
            <div className="d-flex">
              <div style={{ width: "150px", marginRight: "10px" }}>
                <CommonSelect 
                  placeholder="+65" 
                  options={options.phonePrefix} 
                  value={formData.phonePrefix}
                  onChange={(val) => handleChange("phonePrefix", val)}
                />
              </div>
              <div className="flex-grow-1">
                <CommonInput 
                  placeholder="Enter phone number" 
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
            </div>
          </Col>
          <Col md={6} className="mb-3">
            <CommonInput 
              label="Email" 
              placeholder="Enter email" 
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </Col>
        </Row>

        {/* Address Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">Address Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={2} className="mb-3">
            <CommonSelect
              label="Country"
              placeholder="Select an option"
              options={options.country}
              value={formData.country}
              onChange={(val) => handleChange("country", val)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput 
              label="City" 
              placeholder="Enter city" 
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 1"
              placeholder="Enter address line 1"
              value={formData.addressLine1}
              onChange={(e) => handleChange("addressLine1", e.target.value)}
            />
          </Col>
          <Col md={3} className="mb-3">
            <CommonInput
              label="Address Line 2 (Optional)"
              placeholder="Enter address line 2"
              value={formData.addressLine2}
              onChange={(e) => handleChange("addressLine2", e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-3">
            <CommonInput 
              label="Postal Code" 
              placeholder="Enter postal code" 
              value={formData.postalCode}
              onChange={(e) => handleChange("postalCode", e.target.value)}
            />
          </Col>
        </Row>

        {/* ID Information */}
        <div className="bg-light p-2 mb-3 rounded">
          <h6 className="mb-0 fw-bold px-1">ID Information</h6>
        </div>
        <Row className="mb-4">
          <Col md={4} className="mb-3">
            <CommonSelect
              label="ID Type"
              placeholder="Select an option"
              options={options.idType}
              value={formData.idType}
              onChange={(val) => handleChange("idType", val)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonInput 
              label="ID Number" 
              placeholder="Enter ID number" 
              value={formData.idNumber}
              onChange={(e) => handleChange("idNumber", e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-3">
            <CommonDatePicker 
              label="Issue Date" 
              value={formData.issueDate}
              onChange={(val) => handleChange("issueDate", val)}
            />
          </Col>
        </Row>

        {/* Document Upload */}
        <div
          className="bg-light p-2 mb-3 rounded"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <h6 className="mb-0 fw-bold px-1" style={{ color: "#333" }}>
            Document Upload
          </h6>
        </div>
        <Row className="mb-4">
          {[
            {
              id: "frontId",
              label: "Front of ID Card",
              desc: "Upload front image of your ID card",
              small: "Upload front of ID card",
            },
            {
              id: "backId",
              label: "Back of ID Card",
              desc: "Upload back image of your ID card",
              small: "Upload back of ID card",
            },
            {
              id: "selfie",
              label: "Selfie (Optional)",
              desc: "Upload a clear photo of yourself",
              small: "Upload selfie",
            },
            {
              id: "proofAddress",
              label: "Proof of Address (Optional)",
              desc: "Upload document showing your address",
              small: "Upload proof of address",
            },
          ].map((box, i) => (
            <Col md={3} key={i} className="mb-3">
              <label className="text-black fw-bold mb-1 fs-13">
                {box.label}
              </label>
              <input
                type="file"
                className="d-none"
                ref={fileInputRefs[box.id]}
                onChange={(e) => handleFileChange(box.id, e)}
                accept="image/*,.pdf"
              />
              <div
                className="upload-box border-dashed rounded p-2 text-center d-flex flex-column align-items-center justify-content-center"
                style={{
                  border: files[box.id] ? "2px solid var(--primary)" : "2px dashed #d5dfe7",
                  cursor: "pointer",
                  minHeight: "120px",
                  backgroundColor: files[box.id] ? "#f0f9ff" : "#fcfdfe",
                  transition: "all 0.3s ease",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {files[box.id] ? (
                  files[box.id].preview ? (
                    <div className="w-100 h-100 position-relative">
                      <img
                        src={files[box.id].preview}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                      <div
                        className="position-absolute top-50 start-50 translate-middle w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25"
                        style={{ opacity: 0, transition: "0.3s" }}
                        onMouseOver={(e) => (e.currentTarget.style.opacity = 1)}
                        onMouseOut={(e) => (e.currentTarget.style.opacity = 0)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileClick(box.id);
                        }}
                      >
                        <span className="btn btn-xs btn-primary shadow-none">
                          Change
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div onClick={() => handleFileClick(box.id)}>
                      <i className="fas fa-file-alt fs-30 text-primary mb-2"></i>
                      <p
                        className="mb-1 fs-11 text-primary fw-bold text-truncate px-2"
                        style={{ maxWidth: "200px" }}
                      >
                        {files[box.id].file.name}
                      </p>
                      <small className="text-muted fs-10">
                        {(files[box.id].file.size / 1024 / 1024).toFixed(2)} MB
                      </small>
                    </div>
                  )
                ) : (
                  <div onClick={() => handleFileClick(box.id)} className="w-100">
                    <i className="flaticon-381-upload-1 fs-30 text-muted mb-2"></i>
                    <p className="mb-1 fs-11 text-dark fw-bold">{box.desc}</p>
                    <small className="text-muted fs-10">{box.small}</small>
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </form>
    </CommonModal>
  );
};

export default IndividualCustomerModal;
